import React from 'react';
import type { SlideshowCollageLayout, SlideshowPhotoFit, SlideshowMode } from '../../types';

/**
 * Miniature diagrams for the layout / fitting pickers.
 *
 * These choices are inherently visual — "Mosaic" and "4-grid" mean nothing as
 * text labels, and "Fill" vs "Stretch" is impossible to distinguish in words
 * without describing the result. Drawing them makes the control almost
 * self-explanatory. All shapes use currentColor so they inherit the card's
 * selected/unselected state.
 */

const FRAME = { width: 44, height: 26 };

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <svg
      viewBox={`0 0 ${FRAME.width} ${FRAME.height}`}
      width={FRAME.width}
      height={FRAME.height}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const cell = (x: number, y: number, w: number, h: number, key?: string | number) => (
  <rect key={key} x={x} y={y} width={w} height={h} rx="1.5" fill="currentColor" opacity="0.75" />
);

export function CollageDiagram({ layout }: { layout: SlideshowCollageLayout }) {
  switch (layout) {
    case '2-up':
      return <Frame>{[cell(1, 1, 20, 24, 'a'), cell(23, 1, 20, 24, 'b')]}</Frame>;

    case '3-up':
      return (
        <Frame>
          {[cell(1, 1, 13, 24, 'a'), cell(15.5, 1, 13, 24, 'b'), cell(30, 1, 13, 24, 'c')]}
        </Frame>
      );

    case '4-grid':
      return (
        <Frame>
          {[
            cell(1, 1, 20, 11, 'a'),
            cell(23, 1, 20, 11, 'b'),
            cell(1, 14, 20, 11, 'c'),
            cell(23, 14, 20, 11, 'd'),
          ]}
        </Frame>
      );

    case 'mosaic':
    default:
      return (
        <Frame>
          {[
            // Hero, then four supporting frames — the real mosaic proportions.
            cell(1, 1, 24, 24, 'hero'),
            cell(27, 1, 7, 11, 'a'),
            cell(36, 1, 7, 11, 'b'),
            cell(27, 14, 7, 11, 'c'),
            cell(36, 14, 7, 11, 'd'),
          ]}
        </Frame>
      );
  }
}

export function ModeDiagram({ mode }: { mode: SlideshowMode }) {
  if (mode === 'single') {
    return <Frame>{cell(1, 1, 42, 24)}</Frame>;
  }
  if (mode === 'collage') {
    return (
      <Frame>
        {[
          cell(1, 1, 20, 11, 'a'),
          cell(23, 1, 20, 11, 'b'),
          cell(1, 14, 20, 11, 'c'),
          cell(23, 14, 20, 11, 'd'),
        ]}
      </Frame>
    );
  }
  // Mixed — a full frame alongside a divided one.
  return (
    <Frame>
      {[
        cell(1, 1, 20, 24, 'single'),
        cell(23, 1, 9, 11, 'a'),
        cell(34, 1, 9, 11, 'b'),
        cell(23, 14, 9, 11, 'c'),
        cell(34, 14, 9, 11, 'd'),
      ]}
    </Frame>
  );
}

export function FitDiagram({ fit }: { fit: SlideshowPhotoFit }) {
  // The outer dashed rectangle is the screen; the solid shape is the photo.
  const screen = (
    <rect
      x="0.75"
      y="0.75"
      width={FRAME.width - 1.5}
      height={FRAME.height - 1.5}
      rx="2"
      fill="none"
      stroke="currentColor"
      strokeOpacity="0.35"
      strokeDasharray="2 2"
    />
  );

  if (fit === 'fit') {
    // Photo fully visible, bars either side.
    return (
      <Frame>
        {screen}
        <rect x="11" y="3" width="22" height="20" rx="1.5" fill="currentColor" opacity="0.8" />
      </Frame>
    );
  }

  if (fit === 'stretch') {
    // Photo distorted to the frame — the wavy inner line reads as warping.
    return (
      <Frame>
        <rect x="0.75" y="0.75" width={FRAME.width - 1.5} height={FRAME.height - 1.5} rx="2" fill="currentColor" opacity="0.8" />
        <path
          d="M6 13 Q 13 5, 22 13 T 38 13"
          fill="none"
          stroke="var(--bg-surface, #fff)"
          strokeOpacity="0.85"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </Frame>
    );
  }

  // Fill — photo covers the screen, edges cropped (shown overflowing the frame).
  return (
    <Frame>
      <rect x="0.75" y="0.75" width={FRAME.width - 1.5} height={FRAME.height - 1.5} rx="2" fill="currentColor" opacity="0.8" />
      {screen}
      <rect x="0" y="3" width="2.5" height="20" fill="currentColor" opacity="0.35" />
      <rect x={FRAME.width - 2.5} y="3" width="2.5" height="20" fill="currentColor" opacity="0.35" />
    </Frame>
  );
}

/**
 * Transition thumbnails. "Slide" and "Crossfade" are meaningless as words to
 * someone setting up a TV; each mark shows the outgoing and incoming frame and
 * what happens between them.
 */
export function TransitionDiagram({ transition }: { transition: string }) {
  const A = (props: Record<string, number | string>) => (
    <rect rx="1.5" fill="currentColor" opacity="0.8" {...props} />
  );

  switch (transition) {
    case 'slide':
      return (
        <Frame>
          {A({ x: 1, y: 4, width: 17, height: 18 })}
          {A({ x: 26, y: 4, width: 17, height: 18, opacity: 0.35 })}
          <path d="M20 13 h4 m-1.6 -1.8 l1.8 1.8 -1.8 1.8" stroke="currentColor" strokeWidth="1.2"
            fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
        </Frame>
      );

    case 'crossfade':
      return (
        <Frame>
          {A({ x: 3, y: 4, width: 22, height: 18, opacity: 0.75 })}
          {A({ x: 19, y: 4, width: 22, height: 18, opacity: 0.35 })}
        </Frame>
      );

    case 'zoom':
      return (
        <Frame>
          {A({ x: 1, y: 2, width: 42, height: 22, opacity: 0.28 })}
          {A({ x: 9, y: 6, width: 26, height: 14, opacity: 0.85 })}
        </Frame>
      );

    case 'fade-to-black':
      return (
        <Frame>
          {A({ x: 1, y: 4, width: 12, height: 18, opacity: 0.8 })}
          <rect x="15" y="4" width="14" height="18" rx="1.5" fill="currentColor" opacity="0.12" />
          {A({ x: 31, y: 4, width: 12, height: 18, opacity: 0.8 })}
        </Frame>
      );

    case 'kenburns':
    default:
      return (
        <Frame>
          {A({ x: 1, y: 2, width: 42, height: 22, opacity: 0.28 })}
          {A({ x: 5, y: 5, width: 24, height: 16, opacity: 0.85 })}
          <path d="M31 18 l6 -6 m0 0 h-4 m4 0 v4" stroke="currentColor" strokeWidth="1.2"
            fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
        </Frame>
      );
  }
}

/**
 * Before/after for the portrait rule — a conditional behaviour that a sentence
 * alone makes the reader simulate in their head.
 */
export function PortraitDiagram({ whole }: { whole: boolean }) {
  return (
    <svg viewBox="0 0 44 26" width="44" height="26" aria-hidden="true" focusable="false">
      <rect x="0.75" y="0.75" width="42.5" height="24.5" rx="2" fill="none"
        stroke="currentColor" strokeOpacity="0.3" strokeDasharray="2 2" />
      {whole ? (
        // Whole: the full portrait, with space either side.
        <rect x="16" y="3" width="12" height="20" rx="1.5" fill="currentColor" opacity="0.85" />
      ) : (
        // Cropped: the portrait blown up to cover, its top and bottom lost.
        <>
          <rect x="0.75" y="0.75" width="42.5" height="24.5" rx="2" fill="currentColor" opacity="0.85" />
          <rect x="16" y="0" width="12" height="26" fill="currentColor" opacity="0.25" />
        </>
      )}
    </svg>
  );
}
